import { ScatterplotLayer } from '@deck.gl/layers';
import Flatbush from 'flatbush';

type MapLasScatterplotLayerProps = ConstructorParameters<typeof ScatterplotLayer>[0] & {
  _normalize?: boolean;
  filterRange?: readonly [number, number];
};

interface MapLasScatterplotData {
  length: number;
  attributes?: {
    getPosition?: {
      value: ArrayLike<number>;
      size: number;
    };
    getFilterValue?: {
      value: ArrayLike<number>;
      size: number;
    };
  };
}

export class MapLasScatterplotLayer {
  private props: MapLasScatterplotLayerProps;
  private layer: ScatterplotLayer;
  private data: MapLasScatterplotData | undefined;
  private radius: number | null;
  readonly spatialIndex: Flatbush | null;

  constructor(props: MapLasScatterplotLayerProps) {
    this.props = props;
    this.layer = new ScatterplotLayer(props as ConstructorParameters<typeof ScatterplotLayer>[0]);
    this.data = props.data as MapLasScatterplotData | undefined;
    this.spatialIndex = this.createSpatialIndex(this.data);
    const radius = this.layer.props.getRadius;
    this.radius = typeof radius === 'number' ? radius : null;
  }

  getLayer(): ScatterplotLayer {
    return this.layer;
  }

  setVisible(visible: boolean): void {
    if (this.props.visible === visible) return;
    this.props = { ...this.props, visible };
    this.layer = new ScatterplotLayer(this.props as ConstructorParameters<typeof ScatterplotLayer>[0]);
  }

  setFilterRange(filterRange: readonly [number, number]): void {
    if (this.props.filterRange?.[0] === filterRange[0] && this.props.filterRange?.[1] === filterRange[1]) return;
    this.props = { ...this.props, filterRange };
    this.layer = new ScatterplotLayer(this.props as ConstructorParameters<typeof ScatterplotLayer>[0]);
  }

  pick(lat: number, lon: number): number | null {
    const positionAttribute = this.data?.attributes?.getPosition;
    const map = typeof window === 'undefined' ? null : window.mainmap;
    if (!this.spatialIndex || !positionAttribute || !map || this.radius === null || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      return null;
    }

    const candidates = this.spatialIndex.search(lon, lat, lon, lat);
    if (candidates.length === 0) return null;

    let closestIndex = -1;
    let closestDistanceSquared = Infinity;
    let closestLat = 0;
    let closestLon = 0;
    const filterAttribute = this.data.attributes?.getFilterValue;
    const filterRange = this.props.filterRange;
    for (const pointIndex of candidates) {
      if (filterAttribute && filterRange) {
        const filterValue = filterAttribute.value[pointIndex * filterAttribute.size];
        if (filterValue < filterRange[0] || filterValue > filterRange[1]) continue;
      }
      const offset = pointIndex * positionAttribute.size;
      const pointLat = positionAttribute.value[offset + 1];
      const pointLon = positionAttribute.value[offset];
      const meanLatitude = ((lat + pointLat) / 2) * Math.PI / 180;
      const longitudeDistance = (pointLon - lon) * Math.cos(meanLatitude);
      const latitudeDistance = pointLat - lat;
      const distanceSquared = longitudeDistance ** 2 + latitudeDistance ** 2;
      if (distanceSquared < closestDistanceSquared) {
        closestIndex = pointIndex;
        closestDistanceSquared = distanceSquared;
        closestLat = pointLat;
        closestLon = pointLon;
      }
    }
    if (closestIndex === -1) return null;

    const queryPoint = map.latLngToContainerPoint([lat, lon]);
    const closestPoint = map.latLngToContainerPoint([closestLat, closestLon]);
    return queryPoint.distanceTo(closestPoint) <= this.radius ? closestIndex : null;
  }

  private createSpatialIndex(data: MapLasScatterplotData | undefined): Flatbush | null {
    const positionAttribute = data?.attributes?.getPosition;
    if (!data || data.length === 0 || !positionAttribute || positionAttribute.size < 2) {
      return null;
    }

    const index = new Flatbush(data.length);
    for (let pointIndex = 0; pointIndex < data.length; pointIndex += 1) {
      const offset = pointIndex * positionAttribute.size;
      const longitude = positionAttribute.value[offset];
      const latitude = positionAttribute.value[offset + 1];
      index.add(longitude - 0.1, latitude - 0.1, longitude + 0.1, latitude + 0.1);
    }
    index.finish();
    return index;
  }
}